import React from "react";
import PropTypes from "prop-types";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Dialog, { DialogContent, DialogActions } from "components/common/Dialog";
import Button from "components/common/Button";
import TimeSlotInfoCard from "components/CheckInCheckOut/TimeSlotInfoCard";
import "./ShiftSelectionSuccessModal.scss";

function ShiftSelectionSuccessModal({
  open,
  onClose,
  shiftDay,
  shiftStartTime,
  shiftEndTime,
  clockInTime,
  isClockedIn,
  errorCode,
  timezone,
}) {
  const formatTime = (date) => {
    if (!date) return "";
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatShiftTime = () => {
    return `${formatTime(shiftStartTime)} → ${formatTime(shiftEndTime)}`;
  };

  const getTitle = () => {
    if (isClockedIn) {
      return "Your shift has started and your clock-in time was recorded successfully!";
    }
    return "Your shift timings has been recorded";
  };

  const getDescription = () => {
    if (isClockedIn) {
      return "Kindly ensure that your clock-in and clock-out entries are updated regularly each day to maintain accurate attendance records.";
    }

    // Handle error codes
    if (errorCode === "leave") {
      return "Clock-in access is currently unavailable as you are on leave today. Please ensure you return at your scheduled start time to clock in.";
    }
    if (errorCode === "weekend") {
      return "Clock-in access is currently unavailable as today is a weekend. Please ensure you return at your scheduled start time to clock in.";
    }
    if (errorCode === "holiday") {
      return "Clock-in access is currently unavailable as today is a holiday. Please ensure you return at your scheduled start time to clock in.";
    }

    return "Clock-in access will be available 3 hours before your shift begins. Please ensure you return at your scheduled start time to clock in.";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width="444px"
      title={
        <div className="shift-selection-success-modal__title-section">
          <div className="shift-selection-success-modal__icon">
            <CheckCircleIcon />
          </div>
          <h1 className="heading-h1-semibold shift-selection-success-modal__title">{getTitle()}</h1>
        </div>
      }
      content={
        <DialogContent>
          <div className="shift-selection-success-modal__content">
            <p className="para-body-m-regular shift-selection-success-modal__description">{getDescription()}</p>
            <div className="shift-selection-success-modal__info-cards">
              <TimeSlotInfoCard label="Shift day" value={shiftDay} />
              <TimeSlotInfoCard
                label={isClockedIn ? `Shift time (${timezone || "N/A"})` : "Shift time"}
                value={formatShiftTime()}
              />
            </div>
            <div className="w-full">
              <TimeSlotInfoCard
                label="Clock-in time"
                value={isClockedIn ? formatTime(clockInTime) : "Not recorded yet"}
              />
            </div>
          </div>
        </DialogContent>
      }
      actions={
        <DialogActions
          action2={<Button label="Done" size="large" variant="contained" color="primary" onClick={onClose} />}
        />
      }
      size="sm"
    />
  );
}

ShiftSelectionSuccessModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  shiftStartTime: PropTypes.instanceOf(Date),
  shiftEndTime: PropTypes.instanceOf(Date),
  clockInTime: PropTypes.instanceOf(Date),
  isClockedIn: PropTypes.bool,
  errorCode: PropTypes.oneOf(["weekend", "holiday", "leave", undefined]),
  timezone: PropTypes.string,
};

ShiftSelectionSuccessModal.defaultProps = {
  shiftStartTime: null,
  shiftEndTime: null,
  clockInTime: null,
  isClockedIn: false,
  errorCode: undefined,
  timezone: null,
};

export default ShiftSelectionSuccessModal;
